/**
 * Enterprise-grade notification queuing system
 * Handles notification prioritization, deduplication, and intelligent display management
 */

interface QueuedNotification {
  id: string;
  type: 'success' | 'error' | 'info' | 'warning';
  title: string;
  message?: string;
  priority: number; // 1 = highest, 5 = lowest
  timestamp: number;
  dedupKey?: string; // For deduplication
  persistent?: boolean; // Don't auto-dismiss
  actionable?: boolean; // Requires user action
  url?: string; // In-app route to open when the toast is clicked
}

/**
 * Input accepted by enqueue/forceShow: priority is optional and defaults by
 * type (enqueue) or to 1 (forceShow) when omitted.
 */
type NotificationQueueInput = Omit<QueuedNotification, 'id' | 'timestamp' | 'priority'> & {
  priority?: number;
};

interface NotificationQueueConfig {
  maxVisible: number;
  maxQueue: number;
  defaultDuration: number;
  priorityDurations: Record<number, number>;
  dedupWindow: number; // ms
}

class NotificationQueue {
  private static instance: NotificationQueue;
  private queue: QueuedNotification[] = [];
  private visible: QueuedNotification[] = [];
  private dedupMap = new Map<string, number>();
  private config: NotificationQueueConfig = {
    maxVisible: 3,
    maxQueue: 50,
    defaultDuration: 5000,
    priorityDurations: {
      1: 10000, // Critical - 10s
      2: 7000,  // High - 7s
      3: 5000,  // Medium - 5s
      4: 4000,  // Low - 4s
      5: 3000,  // Info - 3s
    },
    dedupWindow: 30000, // 30s
  };

  private constructor() {}

  static getInstance(): NotificationQueue {
    if (!NotificationQueue.instance) {
      NotificationQueue.instance = new NotificationQueue();
    }
    return NotificationQueue.instance;
  }

  /**
   * Add notification to queue with intelligent prioritization
   */
  enqueue(notification: NotificationQueueInput): string {
    const id = this.generateId();
    const timestamp = Date.now();

    const queuedNotification: QueuedNotification = {
      ...notification,
      id,
      timestamp,
      priority: this.calculatePriority(notification.type, notification.priority),
    };

    // Check for deduplication
    if (notification.dedupKey && this.isDuplicate(notification.dedupKey)) {
      return id;
    }

    // Add to dedup map
    if (notification.dedupKey) {
      this.dedupMap.set(notification.dedupKey, timestamp);
    }

    // Add to queue
    this.queue.push(queuedNotification);
    
    // Sort queue by priority and timestamp
    this.sortQueue();
    
    // Trim queue if too large
    if (this.queue.length > this.config.maxQueue) {
      this.queue = this.queue.slice(0, this.config.maxQueue);
    }

    // Process queue
    this.processQueue();

    return id;
  }

  /**
   * Remove notification from queue or visible list
   */
  dequeue(id: string): void {
    this.queue = this.queue.filter(n => n.id !== id);
    this.visible = this.visible.filter(n => n.id !== id);
    this.processQueue();
  }

  /**
   * Get currently visible notifications
   * Highest priority notifications should appear first in the returned list
   */
  getVisible(): QueuedNotification[] {
    return [...this.visible].sort((a, b) => {
      if (a.priority !== b.priority) {
        return a.priority - b.priority;
      }
      return a.timestamp - b.timestamp;
    });
  }

  /**
   * Get queue status
   */
  getStatus(): {
    queueLength: number;
    visibleCount: number;
    hasHighPriority: boolean;
  } {
    return {
      queueLength: this.queue.length,
      visibleCount: this.visible.length,
      hasHighPriority: this.queue.some(n => n.priority <= 2),
    };
  }

  /**
   * Clear all notifications
   */
  clear(): void {
    this.queue = [];
    this.visible = [];
    this.dedupMap.clear();
  }

  /**
   * Process queue and show notifications
   */
  private processQueue(): void {
    // Remove expired dedup entries
    this.cleanupDedupMap();

    // Move notifications from queue to visible if space available
    while (this.visible.length < this.config.maxVisible && this.queue.length > 0) {
      const notification = this.queue.shift()!;
      this.visible.push(notification);
      
      // Set auto-dismiss timer if not persistent
      if (!notification.persistent) {
        const duration = this.config.priorityDurations[notification.priority] || this.config.defaultDuration;
        setTimeout(() => {
          this.dequeue(notification.id);
        }, duration);
      }
    }
  }

  /**
   * Calculate notification priority based on type and explicit priority
   */
  private calculatePriority(type: string, explicitPriority?: number): number {
    if (explicitPriority) {
      return Math.max(1, Math.min(5, explicitPriority));
    }

    // Default priorities by type
    switch (type) {
      case 'error':
        return 1; // Highest priority
      case 'warning':
        return 2;
      case 'success':
        return 4;
      case 'info':
      default:
        return 5; // Lowest priority
    }
  }

  /**
   * Sort queue by priority and timestamp
   */
  private sortQueue(): void {
    this.queue.sort((a, b) => {
      // First by priority (lower number = higher priority)
      if (a.priority !== b.priority) {
        return a.priority - b.priority;
      }
      // Then by timestamp (older first)
      return a.timestamp - b.timestamp;
    });
  }

  /**
   * Check if notification is duplicate
   */
  private isDuplicate(dedupKey: string): boolean {
    const lastSeen = this.dedupMap.get(dedupKey);
    if (!lastSeen) return false;
    
    return Date.now() - lastSeen < this.config.dedupWindow;
  }

  /**
   * Clean up expired dedup entries
   */
  private cleanupDedupMap(): void {
    const now = Date.now();
    for (const [key, timestamp] of this.dedupMap.entries()) {
      if (now - timestamp > this.config.dedupWindow) {
        this.dedupMap.delete(key);
      }
    }
  }

  /**
   * Generate unique notification ID
   */
  private generateId(): string {
    return `notification-${Date.now()}-${Math.random().toString(36).substring(2)}`;
  }

  /**
   * Update configuration
   */
  updateConfig(newConfig: Partial<NotificationQueueConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }

  /**
   * Get configuration
   */
  getConfig(): NotificationQueueConfig {
    return { ...this.config };
  }

  /**
   * Force show high priority notification (bypasses queue)
   */
  forceShow(notification: NotificationQueueInput): string {
    const id = this.generateId();
    const queuedNotification: QueuedNotification = {
      persistent: true, // Don't auto-dismiss (caller may override)
      ...notification,
      id,
      timestamp: Date.now(),
      priority: notification.priority ?? 1, // Force highest priority unless caller set one
    };

    // Remove lowest priority visible notification if at max capacity
    if (this.visible.length >= this.config.maxVisible) {
      const lowestPriority = Math.max(...this.visible.map(n => n.priority));
      const toRemove = this.visible.find(n => n.priority === lowestPriority);
      if (toRemove) {
        this.dequeue(toRemove.id);
      }
    }

    this.visible.push(queuedNotification);
    return id;
  }

  /**
   * Batch operations for performance
   */
  batch(operations: Array<{
    action: 'enqueue' | 'dequeue';
    data: any;
  }>): void {
    operations.forEach(op => {
      if (op.action === 'enqueue') {
        this.enqueue(op.data);
      } else if (op.action === 'dequeue') {
        this.dequeue(op.data);
      }
    });
  }
}

export const notificationQueue = NotificationQueue.getInstance();
export type { QueuedNotification, NotificationQueueInput, NotificationQueueConfig };
